import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ENDPOINT from '../../constants/endpoints';
import { Permission, Role } from '../../helpers/user';
import useApi from '../../hooks/useApi';
import action from '../../pages/CandidateProfile/actions';
import ProfileActions from '../../pages/CandidateProfileOwn/actions';
import ProfileItemPosition from '../../scenes/CandidateProfile/components/ProfileItemPosition';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import BasicSection from '../BasicSection';
import PanelContainer from '../PanelContainer';

const CandidateExperience = ({ user, candidateId, hasEdit = false }) => {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [experience, setExperience] = useState([]);
  const [
    isRecruiter,
    isMasterAccount,
    isMyProfile,
    canDeletePosition,
  ] = useMemo(() => [
    Role.isRecruiter(user),
    Role.isRecruiterGbsMasterAccount(user),
    user.cId === candidateId,
    Permission.isAllowedDeleteCandidatePosition(user.permissions),
  ], [candidateId, user]);

  const handleBeforeSetFirmRating = useCallback(() => {
    return action.beforeSetFirmRating(api, user);
  }, [api, user]);

  const handleSetFirmRating = useCallback((position, mark) => {
    return action.setFirmRating(api, user, { id: position.firmId, mark }).then((data) => {
      setExperience(prev => {
        return prev.map(item => {
          if (item.id === position.id) {
            return {
              ...item,
              rating: data.Rating,
              ratingCount: data.RatingCount,
              candidateRating: true,
            };
          }
          return item;
        });
      });
    });
  }, [api, user]);

  const fetchExperience = useCallback(() => {
    api.getRequest(
      ENDPOINT.ENTITY.CANDIDATE_EXPERIENCE,
      { candidateId },
      {
        handleSuccess: setExperience,
        handleComplete: () => {
          setLoading(false);
        },
      }
    );
  }, [api, candidateId]);

  const handleAddPosition = useCallback(() => {
    ProfileActions.addPosition(
      {
        type: 'position',
        isMyProfile,
        ...(isMyProfile ? { isRecruiter } : { cId: candidateId, isMasterAccount }),
      },
    ).then(fetchExperience);
  }, [candidateId, fetchExperience, isMasterAccount, isMyProfile, isRecruiter]);

  const handleAddRecommendation = useCallback(({ id }) => {
    return ProfileActions.addRecommendation({ PositionID: id, type: 'experience' });
  }, []);

  const handleEditPosition = useCallback(({id}) => {
    return ProfileActions.editPosition(
      {
        id,
        type: 'position',
        isMyProfile,
        ...(isMyProfile ? { isRecruiter } : { cId: candidateId, isMasterAccount })
      },
    ).then(fetchExperience);
  }, [candidateId, fetchExperience, isMasterAccount, isMyProfile, isRecruiter]);

  const handleDeletePosition = useCallback(({ id }) => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete this position?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(
      () => {
        api.post(
          ENDPOINT.ACTION.EDIT_CANDIDATE_DELETE_EXPERIENCE,
          { id, cId: candidateId },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: fetchExperience,
          }
        );
      },
      response => ResponseErrorDialog({ response })
    );
  }, []);

  useEffect(() => {
    fetchExperience();
  }, [fetchExperience]);

  return (
    <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
      <BasicSection
        title="Experience"
        buttons={(isMyProfile || hasEdit) ? [
          {
            caption: 'Add Position',
            handler: handleAddPosition,
          },
        ] : []}
      >
        {experience.map(position => (
          <ProfileItemPosition
            isAddReviewBtnHidden={!isMyProfile}
            data={position}
            actions={{
              onAddRecommendation: isMyProfile ? handleAddRecommendation : null,
              onEditPosition: hasEdit ? handleEditPosition : null,
              onSetFirmRating: handleSetFirmRating,
              onBeforeSetFirmRating: handleBeforeSetFirmRating,
              onDeletePosition: canDeletePosition ? handleDeletePosition : null,
            }}
            key={position.id}
          />
        ))}
      </BasicSection>
    </PanelContainer>
  );
};

export default CandidateExperience;
